import React, { useState, useCallback, useEffect } from "react";
import { TextField, Dropdown, IDropdownOption, Stack } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { SerializedNumberFilter } from "../../../store/tableFilters";
import styled from "styled-components";

type NumberFilterInputProps = {
  value: SerializedNumberFilter;
  onChange: (filter: SerializedNumberFilter) => void;
  min?: number;
  max?: number;
};

export const NumberFilterInput = ({
  value,
  onChange,
  min,
  max,
}: NumberFilterInputProps) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState({
    operator: value.operator,
    number1: value.number1?.toString() || "",
    number2: value.number2?.toString() || "",
  });

  useEffect(() => {
    setLocalValue({
      operator: value.operator,
      number1: value.number1?.toString() || "",
      number2: value.number2?.toString() || "",
    });
  }, [value]);

  const operatorOptions: IDropdownOption[] = [
    { key: "eq", text: t("ram.table.filter.equals") },
    { key: "gt", text: t("ram.table.filter.greaterThan") },
    { key: "lt", text: t("ram.table.filter.lessThan") },
    { key: "between", text: t("ram.table.filter.between") },
  ];

  const handleOperatorChange = useCallback(
    (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        const newOperator = option.key as SerializedNumberFilter["operator"];
        setLocalValue((prev) => ({
          ...prev,
          operator: newOperator,
        }));
        onChange({
          type: "number",
          operator: newOperator,
          number1: localValue.number1 ? parseFloat(localValue.number1) : null,
          number2: localValue.number2 ? parseFloat(localValue.number2) : null,
        });
      }
    },
    [localValue, onChange],
  );

  const handleNumber1Change = useCallback(
    (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string,
    ) => {
      setLocalValue((prev) => ({
        ...prev,
        number1: newValue || "",
      }));
    },
    [],
  );

  const handleNumber2Change = useCallback(
    (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string,
    ) => {
      setLocalValue((prev) => ({
        ...prev,
        number2: newValue || "",
      }));
    },
    [],
  );

  const applyFilter = useCallback(() => {
    onChange({
      type: "number",
      operator: localValue.operator,
      number1: localValue.number1 ? parseFloat(localValue.number1) : null,
      number2: localValue.number2 ? parseFloat(localValue.number2) : null,
    });
  }, [localValue, onChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter") {
        applyFilter();
      }
    },
    [applyFilter],
  );

  return (
    <Container>
      <Stack tokens={{ childrenGap: 8 }}>
        <Dropdown
          selectedKey={localValue.operator}
          options={operatorOptions}
          onChange={handleOperatorChange}
          styles={{
            root: { width: "100%" },
            dropdown: { borderRadius: 4 },
          }}
        />
        <TextField
          type="number"
          value={localValue.number1}
          onChange={handleNumber1Change}
          onBlur={applyFilter}
          onKeyDown={handleKeyDown}
          placeholder={
            localValue.operator === "between"
              ? t("ram.table.filter.minValue")
              : t("ram.table.filter.value")
          }
          min={min}
          max={max}
          styles={{
            root: { width: "100%" },
            fieldGroup: { borderRadius: 4 },
          }}
        />
        {localValue.operator === "between" && (
          <TextField
            type="number"
            value={localValue.number2}
            onChange={handleNumber2Change}
            onBlur={applyFilter}
            onKeyDown={handleKeyDown}
            placeholder={t("ram.table.filter.maxValue")}
            min={min}
            max={max}
            styles={{
              root: { width: "100%" },
              fieldGroup: { borderRadius: 4 },
            }}
          />
        )}
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

export default NumberFilterInput;
